import { useState, useMemo } from 'react';
import { Repeat, ChevronDown, ChevronUp, Flame } from 'lucide-react';
import {
  weekKeys,
  habitAdherence,
  adherenceTotals,
  dormantProjectIds,
  habitWeekSummaries,
  habitsDueOn,
} from '@trackmind/core';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { InfoTip } from '@/components/ui/tooltip';
import { useApp } from '@/context/AppContext';
import { useToday } from '@/hooks/useToday';
import { useTasks } from '@/hooks/useTasks';
import { cn } from '@/lib/utils';

interface HabitsWidgetProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const DAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

/**
 * This week's habits.
 *
 * Rewritten because every number it showed could be wrong at once, and it
 * fetched them the hard way. What changed:
 *
 * - **It reads state instead of polling.** It used to re-query IndexedDB when
 *   the habit *list* changed, plus a 15-second `setInterval` that was switched
 *   off while collapsed — so ticking a habit anywhere else left this stale for
 *   up to fifteen seconds. `habitInstances` on the context is already the full
 *   set and is already updated by `saveHabitInstance`, so the whole thing is
 *   now synchronous and updates on the same render as the tick.
 * - **The header counted the wrong habits.** It showed every non-paused habit,
 *   whether or not one was due today. It now leads with what's due today and
 *   keeps the roster count as the secondary number.
 * - **Completions could outnumber obligations.** The daily total came from the
 *   habits due that day; the daily completed count came from every instance
 *   stored against that date. Complete a habit, then pause it, and the day
 *   read 3/2 with the bar past the end of its track.
 * - **Streaks were capped by the fetch window.** It loaded Monday-to-today and
 *   then walked back a year through it, so on a Monday no streak could exceed
 *   1.
 * - **Monthly habits didn't exist.** The scheduling check handled daily and
 *   weekly and returned false for everything else.
 *
 * All of that logic now lives in `habit-schedule.ts` and is shared with the
 * planner, so the two can't disagree again.
 *
 * Two views: the same card either way, but the phone drops the per-day
 * fraction under each bar (49 characters of 9px text across a 390px screen)
 * in favour of the bar plus the week total, and the details list is capped
 * shorter. Everything a phone hides is still one tap away in Habits.
 */
export function HabitsWidget({ isCollapsed, onToggleCollapse }: HabitsWidgetProps) {
  const { habitInstances, projects } = useApp();
  const { habits } = useTasks();
  const [showDetails, setShowDetails] = useState(false);
  const todayKey = useToday();

  const today = useMemo(() => todayKey(), [todayKey]);
  const days = useMemo(() => weekKeys(today), [today]);
  const todayIndex = days.indexOf(today);

  // Archived habits are history, not obligations. Paused ones are handled
  // per-day inside habitScheduledOn, because a habit paused until Thursday is
  // still due on Friday — filtering them out here would lose that.
  //
  // A habit whose PROJECT is paused, archived or completed is a different
  // case: the project was put away, so its habits are not obligations either.
  // Counting them dragged adherence down for habits nothing was asking for.
  const dormantProjects = useMemo(() => dormantProjectIds(projects), [projects]);
  const roster = useMemo(
    () => habits.filter((h) => !h.is_archived && !dormantProjects.has(h.project_id)),
    [habits, dormantProjects],
  );

  const dueToday = useMemo(() => habitsDueOn(roster, today), [roster, today]);

  const week = useMemo(
    () => habitAdherence(roster, habitInstances, days),
    [roster, habitInstances, days],
  );

  const summaries = useMemo(
    () => habitWeekSummaries(roster, habitInstances, days, today)
      // A habit with nothing due this week is a row of seven blank squares
      // that says nothing. Hidden rather than greyed.
      .filter((s) => s.active)
      .sort((a, b) => b.streak - a.streak || a.title.localeCompare(b.title)),
    [roster, habitInstances, days, today],
  );

  const weekTotals = useMemo(() => {
    // adherenceTotals in core, so the assistant's read_stats reports the
    // same figure this widget shows rather than summing it a second way.
    const totals = adherenceTotals(week);
    return { ...totals, percentage: totals.percentage ?? 0 };
  }, [week]);

  const bestStreak = summaries.find((s) => s.streak > 0) ?? null;
  const doneToday = week[todayIndex]?.completed ?? 0;

  return (
    <Card className="bg-card/50">
      <CardHeader className="py-2 px-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Repeat className="h-4 w-4" />
            Habits
            {dueToday.length > 0 && (
              <span className="text-xs text-muted-foreground tabular-nums">
                {doneToday}/{dueToday.length} today
              </span>
            )}
            {dueToday.length === 0 && roster.length > 0 && (
              <span className="text-xs text-muted-foreground">none due today</span>
            )}
            {roster.length > dueToday.length && (
              <InfoTip
                label={`${dueToday.length} of your ${roster.length} habits are due today. The rest aren't scheduled for today, so they don't count against the day.`}
              >
                <button
                  type="button"
                  aria-label="What this counts"
                  className="text-[10px] leading-none text-muted-foreground rounded-full border border-current h-3.5 w-3.5 flex items-center justify-center"
                >
                  i
                </button>
              </InfoTip>
            )}
          </CardTitle>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onToggleCollapse}>
            {isCollapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
          </Button>
        </div>
      </CardHeader>

      {!isCollapsed && (
        <CardContent className="py-2 px-3">
          {roster.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-2">No active habits</p>
          ) : (
            <div className="space-y-3">
              {/* Daily bars */}
              <div className="space-y-1">
                <div className="flex justify-between gap-1 text-[10px] text-muted-foreground">
                  {days.map((date, i) => (
                    <span
                      key={date}
                      className={cn('flex-1 text-center', i === todayIndex && 'font-bold text-primary')}
                    >
                      {DAY_LABELS[i]}
                    </span>
                  ))}
                </div>
                <div className="flex justify-between gap-1">
                  {week.map((day, i) => (
                    <div key={day.date} className="flex-1">
                      <div
                        className={cn(
                          'h-8 rounded overflow-hidden bg-accent/30 flex flex-col-reverse',
                          i === todayIndex && 'ring-1 ring-primary',
                        )}
                        // A rest day and a day you missed everything both drew
                        // an empty bar. The title is the only thing that told
                        // them apart on desktop; the dash below does it on any
                        // device.
                        title={
                          day.restDay
                            ? `${day.date}: nothing scheduled`
                            : `${day.date}: ${day.completed} of ${day.due} done`
                        }
                      >
                        {!day.restDay && (
                          <div
                            className="bg-green-500/70 transition-all"
                            style={{ height: `${Math.min(100, day.percentage)}%` }}
                          />
                        )}
                      </div>
                      {/* Desktop only: 7 fractions at 9px is unreadable on a
                          390px screen, and the week total below says it. */}
                      <div className="hidden md:block text-[9px] text-center text-muted-foreground mt-0.5 tabular-nums">
                        {day.restDay ? '–' : `${day.completed}/${day.due}`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Week total */}
              <div>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Week</span>
                  <span className="font-medium tabular-nums">
                    {weekTotals.due > 0
                      ? `${weekTotals.completed}/${weekTotals.due} · ${Math.round(weekTotals.percentage)}%`
                      : 'nothing due'}
                  </span>
                </div>
                <Progress value={weekTotals.percentage} className="h-1.5" />
              </div>

              {/* Longest current streak */}
              {bestStreak && (
                <div className="flex items-center gap-1 text-xs min-w-0">
                  <Flame className="h-3 w-3 shrink-0 text-orange-500" />
                  <span className="text-muted-foreground shrink-0">Longest:</span>
                  <span className="font-medium truncate">{bestStreak.title}</span>
                  <span className="text-orange-500 shrink-0">({bestStreak.streak}d)</span>
                </div>
              )}

              {summaries.length > 0 && (
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-left min-h-[32px] md:min-h-0"
                  aria-expanded={showDetails}
                >
                  {showDetails ? '▾ Hide details' : '▸ Show details'}
                </button>
              )}

              {showDetails && (
                <div className="space-y-2 pt-1 border-t border-border/50">
                  {summaries.slice(0, 5).map((habit) => (
                    <div key={habit.id} className="flex items-center gap-2">
                      <span className="text-xs truncate flex-1">{habit.title}</span>
                      {habit.streak > 0 && (
                        <span className="text-[10px] text-orange-500 flex items-center gap-0.5 shrink-0">
                          <Flame className="h-2.5 w-2.5" />
                          {habit.streak}
                        </span>
                      )}
                      <div className="flex gap-0.5 shrink-0">
                        {habit.days.map((day, i) => {
                          const isFuture = todayIndex >= 0 && i > todayIndex;
                          return (
                            <span
                              key={day.date}
                              title={
                                !day.due
                                  ? `${day.date}: not scheduled`
                                  : day.completed
                                    ? `${day.date}: done`
                                    : isFuture
                                      ? `${day.date}: due`
                                      : `${day.date}: missed`
                              }
                              className={cn(
                                'w-2 h-2 rounded-sm',
                                !day.due || isFuture
                                  ? 'bg-muted'
                                  : day.completed
                                    ? 'bg-green-500'
                                    : 'bg-red-500/50',
                              )}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  {summaries.length > 5 && (
                    <p className="text-xs text-muted-foreground">
                      +{summaries.length - 5} more habits
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
